<script setup>
import { ref, computed, nextTick, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";

const props = defineProps({
  email: { type: String, required: true },
});

const emit = defineEmits(["close", "login", "error"]);
const userStore = useUserStore();

const verificationCode = ref(new Array(6).fill(""));
const newPassword = ref("");
const confirmNewPassword = ref("");
const isLoading = ref(false);
const successMessage = ref(null);
const error = ref(null); // Adicionado para seguir o padrão do ForgotPassword
const codeInputRefs = ref([]);

const showPassword = ref(false);
const showConfirmPassword = ref(false);

// --- CRITÉRIOS DE SENHA ---
const passwordCriteriaMet = computed(() => {
  const password = newPassword.value;
  return {
    length: password.length >= 8,
    letter: /[a-zA-Z]/.test(password),
    number: /\d/.test(password),
    special: /[._]/.test(password),
  };
});

const isPasswordValid = computed(() => {
  const criteria = passwordCriteriaMet.value;
  return criteria.length && criteria.letter && criteria.number && criteria.special;
});

const passwordsMismatch = computed(() => {
  const p = newPassword.value?.trim();
  const c = confirmNewPassword.value?.trim();
  return p && c && p !== c;
});

const isFormValid = computed(() => {
  return (
    verificationCode.value.every((digit) => digit !== "") &&
    newPassword.value.trim() !== "" &&
    confirmNewPassword.value.trim() !== "" &&
    isPasswordValid.value &&
    !passwordsMismatch.value
  );
});

// Lógica de Inputs de Código
function handleCodeInput(event, index) {
  const input = event.target;
  if (!/^\d$/.test(input.value)) {
    input.value = "";
    verificationCode.value[index] = "";
    return;
  }
  verificationCode.value[index] = input.value;
  if (input.value && index < 5) {
    codeInputRefs.value[index + 1].focus();
  }
}

function handleCodeBackspace(event, index) {
  if (verificationCode.value[index]) {
    verificationCode.value[index] = "";
  } else if (!event.target.value && index > 0) {
    codeInputRefs.value[index - 1].focus();
  }
}

async function handleSubmit() {
  error.value = null;
  successMessage.value = null;

  if (!isFormValid.value) return;

  isLoading.value = true;
  const finalCode = verificationCode.value.join("");

  try {
    await userStore.resetPassword({
      email: props.email,
      code: finalCode,
      newPassword: newPassword.value,
    });

    successMessage.value = true;
    emit("login", props.email);
  } catch (err) {
    error.value = err.message || "Não foi possível redefinir a senha.";
    emit("error", error.value);
    verificationCode.value.fill("");
    if (codeInputRefs.value[0]) nextTick(() => codeInputRefs.value[0].focus());
    isLoading.value = false;
  }
}

watch(
  () => props.email,
  () => {
    nextTick(() => codeInputRefs.value[0]?.focus());
  },
  { immediate: true }
);
</script>

<template>
  <div class="modal-backdrop">
    <div class="modal-panel">
      <div class="modal-header">
        <h3 class="modal-title">Redefinir Senha</h3>
        <button @click="emit('close')" type="button" class="modal-close-btn">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="modal-body">
          <div v-if="!successMessage">
            <p class="mb-4 text-sm text-gray-600">
              Enviamos um código de seis dígitos para <strong>{{ props.email }}</strong>.
            </p>

            <div class="code-inputs-container">
              <input
                v-for="i in 6"
                :key="i"
                :ref="(el) => { if (el) codeInputRefs[i - 1] = el; }"
                v-model="verificationCode[i - 1]"
                @input="handleCodeInput($event, i - 1)"
                @keydown.delete="handleCodeBackspace($event, i - 1)"
                type="text"
                maxlength="1"
                class="code-input"
              />
            </div>

            <div class="form-group mb-4">
              <label for="newPassword" class="form-label">Nova Senha</label>
              <div class="password-wrapper">
                <input
                  v-model="newPassword"
                  :type="showPassword ? 'text' : 'password'"
                  id="newPassword"
                  class="form-input"
                  :class="{ 'input-error': newPassword && !isPasswordValid }"
                  required
                />
                <button type="button" @click="showPassword = !showPassword" class="password-toggle-btn">
                  <svg v-if="showPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0zM17.938 17.938l-12-12" /></svg>
                </button>
              </div>
              <div class="password-hints">
                <span :class="{ met: passwordCriteriaMet.length }">8+ chars</span>, 
                <span :class="{ met: passwordCriteriaMet.letter }">letras</span>, 
                <span :class="{ met: passwordCriteriaMet.number }">números</span> e 
                <span :class="{ met: passwordCriteriaMet.special }">“.” ou “_”</span>.
              </div>
            </div>

            <div class="form-group">
              <label for="confirmNewPassword" class="form-label label-row">
                Confirmar Senha
                <span v-if="passwordsMismatch" class="label-inline-error">• Diferentes</span>
              </label>
              <div class="password-wrapper">
                <input
                  v-model="confirmNewPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="confirmNewPassword"
                  class="form-input"
                  :class="{ 'input-error': passwordsMismatch }"
                  required
                />
                <button type="button" @click="showConfirmPassword = !showConfirmPassword" class="password-toggle-btn">
                  <svg v-if="showConfirmPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0zM17.938 17.938l-12-12" /></svg>
                </button>
              </div>
            </div>
          </div>

          <div v-else class="success-message">
            Senha redefinida! Redirecionando...
          </div>

          <p v-if="error" class="error-message">{{ error }}</p>
        </div>

        <div class="modal-footer">
          <button type="button" @click="emit('close')" class="btn btn-secondary" :disabled="!!successMessage">
            Cancelar
          </button>
          <button type="submit" class="btn btn-primary" :disabled="isLoading || !isFormValid || !!successMessage">
            {{ isLoading ? 'Redefinindo...' : 'Redefinir Senha' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-width: 28rem;
}

/* Código OTP */
.code-inputs-container {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1.25rem;
}
.code-input {
  width: 2.75rem;
  height: 3rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

/* Senha */
.password-wrapper {
  position: relative;
}
.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-light);
  background: none;
  border: none;
  cursor: pointer;
}

.password-hints {
  font-size: 0.75rem;
  color: var(--text-light);
  margin-top: 0.25rem;
}
.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}

.input-error {
  border-color: var(--danger) !important;
}

.label-inline-error {
  font-size: 0.75rem;
  color: var(--danger);
  font-weight: 600;
}

.error-message {
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 1rem;
  text-align: center;
}

.success-message {
  background-color: #f0fdf4;
  color: var(--success);
  font-weight: 600;
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
}
</style>